import { graphqlClientKoyeb } from "@app/repositories/clients";
import { graphql } from "@generated/client";
import {
    type GetKnowledgeBaseItemsQueryVariables,
    type KnowledgeBaseItemPatch,
    type UpdateCustomItemInput,
} from "@generated/client/graphql";
import { AccessHeadersHolder } from "../AccessHeadersHolder";

const GetKnowledgeBaseItems = graphql(`
    query GetKnowledgeBaseItems($filter: KnowledgeBaseItemFilter!) {
        knowledgeBaseItems(filter: $filter) {
            id
            type
            aiVisibilityStatus
            businessLogicAllowsPoweringAI
            name
            lastModifiedAt
            externalId
            status
            entity {
                id
                name
            }
            document {
                status
                documentType
                uniqueUrl
            }
            indicator {
                entity {
                    id
                    name
                }
            }
            objective {
                entity {
                    id
                    name
                }
                unit
                status
                target {
                    numberValue
                    dueDate
                }
            }
            framework {
                projectName
                cmsId
            }
            customItem {
                description
                value
            }
        }
    }
`);

const UpdateKnowledgeBaseItemMutation = graphql(`
    mutation UpdateKnowledgeBaseItem(
        $id: String!
        $patch: KnowledgeBaseItemPatch!
    ) {
        updateKnowledgeBaseItem(id: $id, patch: $patch) {
            status
        }
    }
`);

const UpdateManyKnowledgeBaseItemsMutation = graphql(`
    mutation UpdateManyKnowledgeBaseItems(
        $ids: [String!]!
        $patch: KnowledgeBaseItemPatch!
    ) {
        updateManyKnowledgeBaseItems(ids: $ids, patch: $patch) {
            status
        }
    }
`);

const CreateCustomKnowledgeBaseItemMutation = graphql(`
    mutation CreateCustomKnowledgeBaseItem(
        $description: String!
        $value: String
    ) {
        createCustomItem(description: $description, value: $value) {
            status
        }
    }
`);

const UpdateCustomKnowledgeBaseItemMutation = graphql(`
    mutation UpdateCustomKnowledgeBaseItem(
        $id: String!
        $patch: UpdateCustomItemInput!
    ) {
        updateCustomItem(id: $id, patch: $patch) {
            status
        }
    }
`);

const DeleteCustomKnowledgeBaseItemMutation = graphql(`
    mutation DeleteCustomKnowledgeBaseItem($id: String!) {
        deleteCustomItem(id: $id) {
            status
        }
    }
`);

export class KnowledgeBaseRepository {
    getKnowledgeBaseItems = async (
        input: GetKnowledgeBaseItemsQueryVariables,
    ) =>
        graphqlClientKoyeb.request(
            GetKnowledgeBaseItems,
            input,
            await AccessHeadersHolder.getHeaders(),
        );

    updateKnowledgeBaseItem = async (
        id: string,
        patch: KnowledgeBaseItemPatch,
    ) =>
        graphqlClientKoyeb.request(
            UpdateKnowledgeBaseItemMutation,
            { id, patch },
            await AccessHeadersHolder.getHeaders(),
        );

    updateManyKnowledgeBaseItems = async (
        ids: string[],
        patch: KnowledgeBaseItemPatch,
    ) =>
        graphqlClientKoyeb.request(
            UpdateManyKnowledgeBaseItemsMutation,
            { ids, patch },
            await AccessHeadersHolder.getHeaders(),
        );

    createCustomKnowledgeBaseItem = async (
        description: string,
        value?: string,
    ) =>
        graphqlClientKoyeb.request(
            CreateCustomKnowledgeBaseItemMutation,
            { description, value },
            await AccessHeadersHolder.getHeaders(),
        );

    updateCustomKnowledgeBaseItem = async (
        id: string,
        patch: UpdateCustomItemInput,
    ) =>
        graphqlClientKoyeb.request(
            UpdateCustomKnowledgeBaseItemMutation,
            { id, patch },
            await AccessHeadersHolder.getHeaders(),
        );

    deleteCustomKnowledgeBaseItem = async (id: string) =>
        graphqlClientKoyeb.request(
            DeleteCustomKnowledgeBaseItemMutation,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
}
